import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PopUpDeniedRequestComponent } from '../pop-up-denied-request/pop-up-denied-request.component';
import { ApiService } from 'src/app/services/api-service/api.service';
import { ActivatedRoute } from '@angular/router';
import { PopUpNotificationComponent } from '../pop-up-notification/pop-up-notification.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-parking-request-details',
  templateUrl: './parking-request-details.component.html',
  styleUrls: ['./parking-request-details.component.css'],
})
export class ParkingRequestDetailsComponent implements OnInit {
  selectedDocumentIndex: number | undefined;

  isLoading = true;
  requestId: string = '';
  parkingRequestId: string = '';
  userRequestDetailsData: any = [];
  documentData: any[] = [];
  dtOptions: DataTables.Settings = {};
  requestType: string = '';

  constructor(
    private dialogRef: MatDialog,
    private route: ActivatedRoute,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';

    this.apiService.getUserRequestById(this.requestId).subscribe(
      (response: any) => {
        this.userRequestDetailsData = response;
        this.requestType = response.type;
        this.isLoading = false;

        response.documents.forEach((document: any) => {
          const documentId = document.id;

          this.apiService
            .getParkingSubscriptionRequestDocuments(documentId)
            .subscribe(
              (documentResponse: any) => {
                this.documentData.push(documentResponse);
              },
              (documentError: any) => {
                console.error('API Error (Document):', documentError);
              }
            );
        });
      },
      (error: any) => {
        console.error('API Error (Request Details):', error);
        this.isLoading = false;
      }
    );
  }

  openDialog(requestId: string) {
    const dialogRef = this.dialogRef.open(PopUpDeniedRequestComponent, {
      data: {
        requestType: this.requestType,
        requestId: requestId,
        userRequestDetailsData: this.userRequestDetailsData,
      },
    });
    dialogRef.afterClosed().subscribe((status: string) => {
      if (status === 'rejected') {
        this.userRequestDetailsData.status = status;
      }
    });
  }

  getDocumentNameByType(type: string): string {
    let name = '';

    switch (type) {
      case 'idFront':
        name = 'Buletin față';
        break;
      case 'idBack':
        name = 'Buletin verso';
        break;
      case 'registrationCertificateFront':
        name = 'Certificat de înmatriculare față';
        break;
      case 'registrationCertificateBack':
        name = 'Certificat de înmatriculare verso';
        break;
      case 'loanAgreement':
        name = 'Contract de comodat';
        break;
      case 'insurance':
        name = 'Asigurare obligatorie';
        break;
      case 'certificateFromTheEmployer':
        name = 'Adeverință de la firmă';
        break;
      default:
        break;
    }

    return name;
  }

  getStatusNameByType(status: string): string {
    let name = '';

    switch (status) {
      case 'accepted':
        name = 'Acceptată';
        break;
      case 'userActionNeeded':
        name = 'În aşteptare documente';
        break;
      case 'pending':
        name = 'Cerere nouă';
        break;
      case 'rejected':
        name = 'Respinsă';
        break;
      case 'active':
        name = 'Activ';
        break;
      case 'expired':
        name = 'Expirat';
        break;
      default:
        break;
    }
    return name;
  }

  getRequestNameByType(type: string): string {
    let name = '';

    switch (type) {
      case 'home':
        name = 'Cerere de parcare la domiciliu';
        break;
      case 'riparian':
        name = 'Cerere de parcare riverani';
        break;
      default:
        break;
    }
    return name;
  }

  acceptDocument(document: any) {
    this.apiService
      .putSubscriptionReqDocById(document.id, 'accepted')
      .subscribe(
        () => {
          document.status = 'accepted';
        },
        (error) => {
          console.error('Error accepting document:', error);
        }
      );
  }

  declineDocument(document: any) {
    this.apiService
      .putSubscriptionReqDocById(document.id, 'rejected')
      .subscribe(
        () => {
          document.status = 'rejected';

          // Check if there are other documents with status !== 'accepted'
          const unAcceptedDocuments = this.documentData?.filter(
            (doc: { status: string; required: any; url: any }) => {
              return doc.status !== 'accepted' || (!doc.required && !doc.url);
            }
          );

          // If there are unaccepted documents, set the request status to 'userActionNeeded'
          if (unAcceptedDocuments.length > 0) {
            const rejectionReason = '';
            this.apiService
              .putSubscriptionRequestById(
                this.requestId,
                'userActionNeeded',
                rejectionReason
              )
              .subscribe(
                (response) => {
                  if (
                    response.message ===
                    'Changes have been successfully applied.'
                  ) {
                    this.userRequestDetailsData.status = 'userActionNeeded';
                  } else {
                    console.error('Request status update failed.');
                  }
                },
                (error) => {
                  console.error('Error updating request status:', error);
                }
              );
          }
        },
        (error) => {
          console.error('Error rejecting document:', error);
        }
      );
  }

  isAcceptButtonEnabled(): boolean {
    let unAcceptedDocuments = this.documentData?.filter(
      (doc: { status: string; required: any; url: any }) => {
        return doc.status !== 'accepted' || (!doc.required && !doc.url);
      }
    );
    return !unAcceptedDocuments?.length;
  }

  acceptRequest(status: string, rejectionReason: string) {
    const dialogRef = this.dialogRef.open(ConfirmationDialogComponent, {
      width: '400px',
    });

    dialogRef.componentInstance.message =
      'Ești sigur că vrei să accepți această cerere?';
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.apiService
          .putSubscriptionRequestById(this.requestId, status, rejectionReason)
          .subscribe(
            (response) => {
              if (
                response.message === 'Changes have been successfully applied.'
              ) {
                this.userRequestDetailsData.status = 'accepted';
              } else {
                console.error('Request acceptance failed. Status not updated.');
              }
            },
            (error) => {
              console.error('Error accepting request:', error);
            }
          );
      }
    });
  }

  sendNotification() {
    const dialogRef = this.dialogRef.open(PopUpNotificationComponent, {
      width: '400px',
    });
  }
}
